import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface HypeOverlayProps {
  active: boolean
  phrases?: string[]
  intervalSeconds?: number
  maxMessages?: number
}

const DEFAULT_PHRASES = [
  "Ready for your best photo ever? 📸",
  "Strike a pose. Make it iconic. ✨",
  "Step in. Light up the night. 🔥",
  "Warning: You might look too good. 💅",
  "3… 2… 1… Iconic.",
  "Own the spotlight. 🌟",
  "Tonight, you're the main event.",
  "Best. Photo. Ever.",
  "Step inside & smile!",
  "Made for the night. 💜",
  "Your moment. Your light. Your shot.",
  "The dance floor called. It wants a photo.",
  "Get in here. You're up! 🎉",
  "Lights on. Camera ready. Let's go.",
]

const GLOW_COLORS = {
  purple: 'rgba(180, 0, 255, 0.9)',
  hotPink: 'rgba(255, 0, 180, 0.6)',
  electricBlue: 'rgba(0, 207, 255, 0.9)',
  amber: 'rgba(255, 170, 0, 0.9)',
}

interface ActivePhrase {
  id: string
  text: string
  x: number
  y: number
  fontSize: string
  color: string
  rotation: string
}

export default function HypeOverlay({ 
  active, 
  phrases = DEFAULT_PHRASES, 
  intervalSeconds = 2.5,
  maxMessages = 4
}: HypeOverlayProps) {
  const [activePhrases, setActivePhrases] = useState<ActivePhrase[]>([])
  const [, setQueue] = useState<string[]>([])
  const lastPosRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!active || phrases.length === 0) {
      setActivePhrases([])
      setQueue([])
      return
    }

    const interval = setInterval(() => {
      setQueue((currentQueue) => {
        let q = [...currentQueue]
        if (q.length === 0) {
          q = [...phrases].sort(() => Math.random() - 0.5)
        }
        const text = q[0]
        const remainingQueue = q.slice(1)

        setActivePhrases((prev) => {
          if (prev.length >= maxMessages) return prev // Max phrases

          // Find a random position avoiding the previous one
          // Restrict X to 5%-40% to prevent clipping off the right edge
          let x = 5 + Math.random() * 35
          let y = 10 + Math.random() * 65

          if (lastPosRef.current) {
            const dist = Math.sqrt(
              Math.pow(x - lastPosRef.current.x, 2) + 
              Math.pow(y - lastPosRef.current.y, 2)
            )
            if (dist < 15) {
              // Try again once
              x = 5 + Math.random() * 35
              y = 10 + Math.random() * 65
            }
          }

          lastPosRef.current = { x, y }

          const colors = Object.values(GLOW_COLORS)
          const randomColor = colors[Math.floor(Math.random() * colors.length)]
          const randomFontSize = `${(1.8 + Math.random() * 1).toFixed(1)}rem`
          const randomRotation = `${(Math.random() * 8 - 4).toFixed(1)}deg`

          const newPhrase: ActivePhrase = {
            id: Math.random().toString(36).substring(2, 9),
            text,
            x,
            y,
            fontSize: randomFontSize,
            color: randomColor,
            rotation: randomRotation,
          }

          setTimeout(() => {
            setActivePhrases((current) => current.filter((p) => p.id !== newPhrase.id))
          }, 4000) // Fixed lifetime of 4s for each phrase

          return [...prev, newPhrase]
        })

        return remainingQueue
      })
    }, intervalSeconds * 1000)

    return () => clearInterval(interval)
  }, [active, phrases, intervalSeconds])

  const [flash, setFlash] = useState(false)
  
  useEffect(() => {
    if (!active) return
    const interval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% chance every 5s
        setFlash(true)
        setTimeout(() => setFlash(false), 150)
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [active])

  if (!active) return null

  return (
    <div 
      className="fixed inset-0 z-[9999] pointer-events-none select-none overflow-hidden"
      style={{ fontFamily: "'Montserrat', 'Inter', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&display=swap');
      `}</style>

      {/* Camera Flash Effect */}
      {flash && <div className="absolute inset-0 bg-white z-[10000]" />}

      {/* Camera Viewfinder Overlay */}
      <div className="absolute inset-0 border-[20px] border-transparent p-6">
        {/* Top Left */}
        <div className="absolute top-6 left-6 w-10 h-10 border-t-4 border-l-4 border-white/40 rounded-tl-sm" />
        {/* Top Right */}
        <div className="absolute top-6 right-6 w-10 h-10 border-t-4 border-r-4 border-white/40 rounded-tr-sm" />
        {/* Bottom Left */}
        <div className="absolute bottom-6 left-6 w-10 h-10 border-b-4 border-l-4 border-white/40 rounded-bl-sm" />
        {/* Bottom Right */}
        <div className="absolute bottom-6 right-6 w-10 h-10 border-b-4 border-r-4 border-white/40 rounded-br-sm" />
        
        {/* REC Dot */}
        <div className="absolute top-10 right-10 flex items-center gap-2">
          <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
          <span className="text-white text-xs font-bold tracking-widest opacity-70">REC</span>
        </div>
        
        {/* Battery */}
        <div className="absolute top-10 left-10 flex items-center gap-1 opacity-40">
          <div className="w-6 h-3 border border-white rounded-sm p-0.5">
            <div className="w-full h-full bg-white" />
          </div>
          <div className="w-0.5 h-1 bg-white" />
        </div>

        {/* Center Crosshair */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 opacity-30">
          <div className="absolute top-1/2 left-0 w-full h-px bg-white" />
          <div className="absolute left-1/2 top-0 w-px h-full bg-white" />
        </div>
      </div>
      
      <AnimatePresence>
        {activePhrases.map((phrase) => (
          <motion.div
            key={phrase.id}
            initial={{ opacity: 0, x: -30, filter: 'blur(10px)', scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              x: 0, 
              filter: 'blur(0px)',
              scale: [1, 1.02, 1],
              transition: {
                opacity: { duration: 0.6, ease: 'easeOut' },
                x: { duration: 0.6, ease: 'easeOut' },
                filter: { duration: 0.6, ease: 'easeOut' },
                scale: { duration: 3, ease: 'easeInOut', repeat: Infinity }
              }
            }}
            exit={{ opacity: 0, x: 30, filter: 'blur(10px)', transition: { duration: 0.4, ease: 'easeIn' } }}
            style={{
              position: 'absolute',
              left: `${phrase.x}%`,
              top: `${phrase.y}%`,
              fontSize: phrase.fontSize,
              fontWeight: 800,
              color: 'white',
              letterSpacing: '0.05em',
              transform: `rotate(${phrase.rotation})`,
              textShadow: `0 0 20px ${phrase.color}, 0 0 40px ${phrase.color}, 0 0 4px rgba(0,0,0,1)`,
              whiteSpace: 'nowrap',
            }}
          >
            {phrase.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
